import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { Button, Form, Modal, Ref } from 'semantic-ui-react'
import React, { useState } from 'react'
import {
  BtnClearFormatting,
  BtnStrikeThrough,
  BtnNumberedList,
  EditorProvider,
  BtnBulletList,
  BtnUnderline,
  HtmlButton,
  Separator,
  BtnItalic,
  Toolbar,
  BtnLink,
  BtnRedo,
  BtnBold,
  BtnUndo,
  Editor,
} from 'react-simple-wysiwyg'

import NoteTemplatesDropdown from '../NoteTemplatesDropdown'
import SaveAsTemplateBtn from '../SaveAsTemplateBtn'
import { classNames } from '../../utils'
import { Task } from '../../types'

type FormValues = {
  doneNote: string
}

interface Props {
  onMarkAsDone(task: Task): Promise<any>
  onClose(): void
  task: Task
}

export default function FulfillTaskModal(props: Props) {
  const { onMarkAsDone, onClose, task } = props

  const [isSaving, setIsSaving] = useState(false)

  const form = useForm<FormValues>({
    defaultValues: { doneNote: '' },
  })

  const onSave: SubmitHandler<FormValues> = async (data) => {
    setIsSaving(true)
    return onMarkAsDone({ ...task, doneNote: data.doneNote }).finally(() =>
      setIsSaving(false),
    )
  }

  return (
    <Modal
      closeOnDimmerClick={!isSaving}
      closeOnEscape={!isSaving}
      onClose={onClose}
      size="small"
      open
    >
      <Modal.Header className="!text-3xl">Fulfill Task</Modal.Header>

      <Modal.Content className="!pt-0">
        <Form
          onSubmit={form.handleSubmit(onSave)}
          spellCheck="false"
          id="fulfill-task"
        >
          <div>
            <label className="mb-1 block text-base font-semibold leading-none">
              Task
            </label>
            {task.subject}
          </div>

          {task.details && (
            <div className="mt-6">
              <label className="mb-1 block text-base font-semibold leading-none">
                Details
              </label>
              <div dangerouslySetInnerHTML={{ __html: task.details }} />
            </div>
          )}

          <div>
            <Controller
              name="doneNote"
              control={form.control}
              rules={{
                required: 'Required',
                minLength: { value: 4, message: 'Provide a longer note' },
              }}
              render={({ field, fieldState }) => {
                const isDisabled = form.formState.isSubmitting
                const { ref, ...other } = field
                return (
                  <div
                    className={classNames(
                      'relative mt-6',
                      form.formState.errors.doneNote && 'rsw-has-error',
                      isDisabled && 'rsw-is-disabled',
                    )}
                  >
                    <label className="mb-2 block text-base font-semibold leading-none">
                      Provide a note to mark this task as done:
                    </label>
                    <div className="relative">
                      <EditorProvider>
                        <Ref innerRef={ref}>
                          <Editor
                            {...other}
                            autoFocus
                            disabled={isDisabled}
                            containerProps={{
                              className: 'rsw-editor no-overrides mt-4',
                            }}
                            // @ts-ignore
                            placeholder="Say something smart..."
                          >
                            <Toolbar>
                              <BtnUndo />
                              <BtnRedo />
                              <Separator />
                              <BtnBold />
                              <BtnItalic />
                              <BtnUnderline />
                              <BtnStrikeThrough />
                              <Separator />
                              <BtnNumberedList />
                              <BtnBulletList />
                              <Separator />
                              <BtnLink />
                              <BtnClearFormatting />
                              <HtmlButton />
                              <div className="h-5 w-[110px]" />
                            </Toolbar>
                          </Editor>
                        </Ref>
                      </EditorProvider>
                      <div className="absolute right-1.5 top-[5px]">
                        <NoteTemplatesDropdown
                          disabled={isDisabled}
                          onChange={other.onChange}
                        />
                      </div>
                    </div>
                    <div className="text-base leading-normal text-red-600 empty:hidden">
                      {fieldState.error?.message}
                    </div>
                  </div>
                )
              }}
            />
          </div>
        </Form>
        <div className="mt-8 flex justify-end gap-2">
          <Button
            onClick={props.onClose}
            content="Nevermind..."
            type="button"
            className="mr-0"
            disabled={isSaving}
            basic
          />
          <SaveAsTemplateBtn
            text={form.watch('doneNote')}
            askForName
            disabled={isSaving}
          />
          <Button
            loading={isSaving}
            disabled={isSaving}
            content="Mark as done!"
            icon="save"
            primary
            type="submit"
            className="mr-0"
            form="fulfill-task"
          />
        </div>
      </Modal.Content>
    </Modal>
  )
}
